import { useState } from "react"
import type { ReactNode } from "react"

import { BrandInitialMonogram } from "@/components/brand-initial-monogram"
import { BRAND_COVER_ASPECT_CLASS } from "@/lib/brand-cover"
import { cn } from "@/lib/utils"

/**
 * OKS-304 — Shared brand-cover hero band.
 *
 * Mounted by every consumer-facing surface that surfaces the brand
 * cover (TRD §4 / Tasks #13–#15):
 *   - `Invitations/Accept.tsx` — full-bleed above the accept card on
 *     the `valid` state
 *   - `Onboarding/Welcome.tsx` — between the WizardShell header and
 *     the centred main
 *   - `Creator/Missions.tsx` — first child of content area, ALWAYS
 *     rendered as page chrome (TRD K5 / UX Spec §2.4 amendment)
 *
 * OKS-855 — the `default` variant retargets from a shallow 3:1 strip to a
 * 3:2 frame (same ratio as mission covers), reading the single-source
 * `BRAND_COVER_ASPECT_CLASS` token (`lib/brand-cover.ts`) so it can never
 * drift back to 3:1. This band is the SINGLE display choke point — every
 * default-variant consumer (creator home, invite-accept, invite-landing
 * default header, settings preview) routes through it, so the flip retargets
 * them all at once. Legacy 3:1-era attachments center-crop gracefully into
 * the taller frame (`object-cover`, no letterbox). The `identity` variant is
 * intentionally NOT aspect-locked and keeps its bespoke tall hero.
 *
 * Behaviour:
 *   - When `coverUrl` is a non-null string → renders the image at
 *     `BRAND_COVER_ASPECT_CLASS object-cover` (3:2) with `loading="eager"`
 *     (above-the-fold) and `alt=""` (decorative — the brand name is
 *     announced by the page heading per PRD §6 / UX Spec §6).
 *   - When `coverUrl` is null OR the image fails to load (browser
 *     cache eviction past the underlying S3 presigned URL's
 *     `service_urls_expire_in`, mid-render attachment purge, etc.) →
 *     renders the dark fallback band (`bg-surface-inverse`) with a
 *     centred `<BrandInitialMonogram />` per UX Spec §2.1 / R7.
 *
 * The fallback is a feature of the design, not an accident — admins
 * can intentionally not upload a cover and the surface stays clean.
 *
 * Brand isolation: this component takes the cover URL + brand name
 * directly as props. There is no global brand-cover store; every
 * consumer page passes its server-resolved (Current.brand or
 * invitation-bound) values. Aligns with TRD K2 invariant.
 */
export interface BrandCoverBandProps {
  /** Signed cover URL from the server, or null when no cover attached
   *  / brand context unavailable (e.g. creator with no membership). */
  coverUrl: string | null | undefined
  /** Brand display name — used by the dark-fallback monogram when
   *  `coverUrl` is null. Falls back to "?" when blank. */
  brandName: string | null | undefined
  /** Tailwind extras (rounded corners, max-width caps). Each consumer
   *  surface tunes the chrome; the band's intrinsic 3:2 ratio (OKS-855) +
   *  dark fallback are constants. */
  className?: string
  /** Test id forwarded for assertion contracts (request specs +
   *  Playwright). Defaults to "brand-cover-band". */
  testId?: string
  /**
   * OKS-855 — forwarded to the cover `<img>`'s `onLoad`. Lets a consumer
   * (the settings uploader) gate an edit-load skeleton on the first paint of
   * a server-provided cover, mirroring `MasterclassCoverImageField`. Fires
   * ONLY on the `default`/`identity` image branch (never for the dark
   * fallback, which has no `<img>`). Omitted → no behavioural change.
   */
  onImageLoad?: () => void
  /**
   * OKS-855 — forwarded to the cover `<img>`'s `onError` (fires alongside the
   * band's own internal degrade-to-dark-fallback). Lets a consumer SETTLE an
   * edit-load skeleton when the server cover resolves the OTHER way — a 404
   * (expired/purged signed URL) fires `onError`, never `onLoad`, so a
   * load-only gate would leave the skeleton (and a `hidden` band) stuck
   * forever, masking the graceful dark fallback (OKS-304 contract). Fires
   * ONLY on the image branch, before the swap to the dark fallback. Omitted →
   * no behavioural change (the band still degrades on its own).
   */
  onImageError?: () => void
  /**
   * OKS-383 — band treatment:
   *   - `"default"` — bare 3:2 (OKS-855) cover/monogram band, used by
   *     Accept / Welcome / Missions as page chrome.
   *   - `"identity"` — premium invite-landing cover: taller band with a
   *     bottom-up gradient for text legibility and a centred overlay
   *     slot (`children`) carrying the logo + greeting + inviter line
   *     (OKS-457 §01 / UX Spec §2.2). Full-bleed `w-full` — spans the
   *     entire 402px mobile reference with no clamp (OKS-457 KD-5; the
   *     "390px clamp" was a Figma-authoring artifact, never a code clamp).
   * Defaults to `"default"` so existing consumers render byte-identically.
   */
  variant?: "default" | "identity"
  /** Overlay content for the `identity` variant (logo / eyebrow /
   *  headline / inviter row). Ignored by the `default` variant. */
  children?: ReactNode
  /**
   * OKS-716 — integrated brand identity for the `default` variant.
   *
   * When provided, the default 3:2 band renders a bottom-LEFT identity
   * overlay (small logo/initials monogram + brand name) so the brand
   * reads as part of the cover on the creator home. Rendered over BOTH
   * the cover image (behind a bottom-up scrim for WCAG-AA legibility)
   * AND the dark no-cover fallback band (which already provides its own
   * contrast, so no scrim there). `null` / omitted → the default band
   * renders byte-identically to pre-OKS-716 (bare cover, or centred
   * monogram fallback).
   *
   * Ignored by `variant="identity"` (the invite landing keeps its
   * centred `children` overlay untouched).
   *
   * `logoUrl` null → the overlay shows the initials monogram instead of
   * an `<img>`. `name` is REAL announced text (the cover `<img>` stays
   * `alt=""` / decorative — PRD §6 / UX Spec §6).
   */
  identity?: { name: string; logoUrl: string | null }
}

export function BrandCoverBand({
  coverUrl,
  brandName,
  className,
  testId = "brand-cover-band",
  variant = "default",
  children,
  identity,
  onImageLoad,
  onImageError,
}: BrandCoverBandProps) {
  // Track image-load failure so a stale signed URL or mid-render purge
  // gracefully degrades to the dark-fallback band instead of showing the
  // browser's broken-image icon. TRD §8 risk mitigation.
  const [imageBroken, setImageBroken] = useState(false)

  // OKS-855 — degrade to the dark fallback AND notify the consumer so it can
  // settle an edit-load skeleton on the error path (a 404 never fires onLoad).
  const handleImageError = () => {
    setImageBroken(true)
    onImageError?.()
  }

  const showImage = Boolean(coverUrl) && !imageBroken
  const monogramName = (brandName ?? "").trim() || "?"

  // OKS-716 — integrated identity overlay for the default variant. Anchored
  // bottom-LEFT (the invite `identity` variant is centred). `min-w-0` +
  // `truncate` keep a long brand name to one line inside the rounded band at
  // 375px with no horizontal overflow (OKS-749/808 overflow discipline). The
  // name is real text; the logo `<img>` is decorative (`alt=""`), the name
  // beside it is the announced identity.
  const identityOverlay = identity ? (
    <div
      data-testid={`${testId}-identity`}
      className="absolute inset-x-0 bottom-0 z-10 flex items-center gap-2 px-4 pb-3 text-white"
    >
      {identity.logoUrl ? (
        <img
          src={identity.logoUrl}
          alt=""
          className="size-6 shrink-0 rounded-full object-cover ring-1 ring-white/30"
        />
      ) : (
        <BrandInitialMonogram
          name={identity.name}
          className="size-6 shrink-0 bg-white/15 text-xs text-white"
          testId={`${testId}-identity-monogram`}
        />
      )}
      <span
        className="min-w-0 truncate text-sm font-semibold"
        title={identity.name}
      >
        {identity.name}
      </span>
    </div>
  ) : null

  // Bottom-up scrim so white identity text stays WCAG-AA legible over a
  // light/busy cover image. Only needed on the IMAGE branch — the dark
  // fallback band already provides contrast.
  const identityScrim = identity ? (
    <div
      aria-hidden="true"
      className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"
    />
  ) : null

  // OKS-383 — identity variant: image (or dark fallback) + gradient +
  // centred overlay. `data-state` mirrors the default contract
  // (image | fallback) so QA can assert the cover branch; `data-variant`
  // discriminates the treatment.
  if (variant === "identity") {
    return (
      <div
        data-testid={testId}
        data-state={showImage ? "image" : "fallback"}
        data-variant="identity"
        className={cn(
          "bg-surface-inverse relative flex min-h-72 w-full flex-col items-center justify-end overflow-hidden",
          className,
        )}
      >
        {showImage && (
          <img
            src={coverUrl ?? undefined}
            alt=""
            loading="eager"
            decoding="async"
            onError={handleImageError}
            data-testid={`${testId}-image`}
            className="absolute inset-0 size-full object-cover"
          />
        )}
        {/* Bottom-up gradient for white-text legibility over any cover. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/20"
        />
        <div
          data-testid={`${testId}-overlay`}
          className="relative z-10 flex w-full flex-col items-center gap-3 px-6 pt-14 pb-8 text-center text-white"
        >
          {children}
        </div>
      </div>
    )
  }

  if (showImage) {
    return (
      <div
        data-testid={testId}
        data-state="image"
        className={cn(
          BRAND_COVER_ASPECT_CLASS,
          "bg-surface-inverse relative w-full overflow-hidden",
          className,
        )}
      >
        <img
          src={coverUrl ?? undefined}
          alt=""
          loading="eager"
          decoding="async"
          onError={handleImageError}
          onLoad={onImageLoad}
          data-testid={`${testId}-image`}
          className="size-full object-cover"
        />
        {/* OKS-716 — integrated identity over the cover image (scrim first
            for legibility, then the bottom-left logo + name). */}
        {identityScrim}
        {identityOverlay}
      </div>
    )
  }

  // Empty / failed-load fallback — dark band.
  //
  // OKS-716 — when `identity` is provided (creator home), the dark band
  // carries the SAME bottom-left identity overlay so the no-cover home still
  // reads as branded (UX Spec §3). No scrim needed — the band is already dark.
  if (identity) {
    return (
      <div
        data-testid={testId}
        data-state="fallback"
        className={cn(
          BRAND_COVER_ASPECT_CLASS,
          "bg-surface-inverse relative w-full overflow-hidden",
          className,
        )}
      >
        {identityOverlay}
      </div>
    )
  }

  // Bare fallback (no integrated identity) — dark band + centred monogram.
  // `bg-foreground/10` (the monogram tile's default) is mostly invisible
  // against `bg-surface-inverse`; passing an explicit on-dark variant
  // class keeps the initial visible.
  return (
    <div
      data-testid={testId}
      data-state="fallback"
      className={cn(
        BRAND_COVER_ASPECT_CLASS,
        "bg-surface-inverse relative w-full overflow-hidden",
        "flex items-center justify-center",
        className,
      )}
    >
      <BrandInitialMonogram
        name={monogramName}
        className="size-20 bg-white/10 text-3xl text-white"
        testId={`${testId}-fallback`}
      />
    </div>
  )
}

export default BrandCoverBand
